/**
 * Serializable video editor project for draft storage (local or future API).
 */

export const VIDEO_EDIT_DRAFT_SCHEMA_VERSION = 1 as const;

export interface VideoEditDraftAnnotation {
  id: string;
  type: 'text' | 'rect' | 'arrow' | 'circle' | 'line' | 'image' | 'emoji' | 'audio' | 'blur';
  x: number;
  y: number;
  w: number;
  h: number;
  text: string;
  color: string;
  fontSize: number;
  inTime: number;
  outTime: number;
  fontFamily?: string;
  backgroundColor?: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  textAlign?: 'left' | 'center' | 'right' | 'justify';
  textBoxWidthRatio?: number;
  /** Rect / circle — fill inside stroke; omit or transparent = no fill */
  fillColor?: string;
  /** Image — data URL or remote URL */
  imageSrc?: string;
  /** Audio — data URL or blob URL (data URL recommended for drafts) */
  audioSrc?: string;
  /** Audio clip duration in seconds (editor sync / export) */
  audioDurationSec?: number;
  /** Audio clip gain 0..1 (UI + export); default 1 */
  audioVolume?: number;
  /** Blur — style and block size in px */
  blurStyle?: 'pixelate' | 'blur';
  blurStrength?: number;
}

export interface VideoEditDraftProject {
  version: typeof VIDEO_EDIT_DRAFT_SCHEMA_VERSION;
  inPoint: number;
  outPoint: number;
  annotations: VideoEditDraftAnnotation[];
  mode: 'basic' | 'advanced';
  timelineZoom: number;
  saveFileName: string;
  /** Background / padding / corners / shadow around the video (optional; older drafts have none). */
  frame?: import('../lib/videoFrame').VideoFrameStyle;
  /** Zoom segments (optional; older drafts have none). */
  zooms?: import('../lib/videoZoom').ZoomSegment[];
  /** Splits and removed parts (optional; older drafts have none). */
  cuts?: import('../lib/videoCuts').VideoCuts;
}

/** Enough to reload the source asset in the editor */
export interface VideoEditDraftSourceSnapshot {
  id: string;
  title: string;
  /** Kept on the edited copy saved to My Library. */
  description?: string;
  url: string;
  streamUrl?: string;
  file_url?: string;
  duration?: number;
}

export interface VideoEditDraft {
  id: string;
  createdAt: string;
  updatedAt: string;
  title: string;
  source: VideoEditDraftSourceSnapshot;
  project: VideoEditDraftProject;
}
